import React, { useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AppSettings } from './types';

// Helper function to create default settings without overriding app_background_color
const createDefaultSettings = (): AppSettings => ({
  id: 1,
  typography: { source: 'google', googleFont: 'Inter', customFontUrl: null },
  languages: { default: 'en', enabled: ['en', 'he', 'ar', 'fr', 'es'] },
  theme: { 
    type: 'preset', 
    preset: 'agam', 
    colors: null,
    app_background_color: null // Don't override with hardcoded value
  },
  home_settings_v2: {
    logoUrl: '/lovable-uploads/727626ab-d59a-4f99-aed6-fe8a671929b5.png',
    showLogo: true,
    elementOrder: ['logo', 'welcomeMessage', 'exhibitAccess', 'visitorInfo'],
    showVisitorInfo: true,
    showExhibitAccess: true,
    showWelcomeMessage: true,
    exhibitAccessButtons: { scanQR: true, favorites: true, browseTours: true, enterNumber: true },
    showBackgroundShapes: true,
    showHomeImage: false,
    homeImageSettings: {
      imageUrl: '',
      altText: '',
      caption: '',
      size: 'medium' as const
    },
    welcomeMessageCustom: {},
    useCustomWelcomeMessage: false
  },
  button_labels: {}
});

interface AppSettingsProviderProps {
  children: ReactNode;
  onSettingsChange: (settings: AppSettings | null) => void;
  onLoadingChange: (loading: boolean) => void;
  onSavingChange: (saving: boolean) => void;
}

export function AppSettingsProvider({ 
  children, 
  onSettingsChange, 
  onLoadingChange, 
  onSavingChange 
}: AppSettingsProviderProps) {
  // Create default settings for compatibility
  const defaultSettings = createDefaultSettings();
  
  useEffect(() => {
    const loadSettings = async () => {
      try {
        console.log('📝 [AppSettingsProvider] Loading settings...');
        onLoadingChange(true);
        
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('👤 [AppSettingsProvider] User authenticated - loading from app_settings');
          // Authenticated user - use direct table access
          const { data, error } = await supabase
            .from('app_settings')
            .select('*')
            .eq('id', 1)
            .maybeSingle();

          if (error) {
            console.error('❌ [AppSettingsProvider] Database error:', error);
            console.log('📝 [AppSettingsProvider] Using default settings as fallback');
            onSettingsChange(defaultSettings);
          } else if (data) {
            console.log('✅ [AppSettingsProvider] Loaded settings from database:', data);
            
            // Transform database row to AppSettings format
            const settings: AppSettings = {
              id: data.id,
              typography: (data.typography as any) || { source: 'google', googleFont: 'Inter', customFontUrl: null },
              languages: (data.languages as any) || { default: 'en', enabled: ['en', 'he', 'ar', 'fr', 'es'] },
              theme: (data.theme as any) || { type: 'preset', preset: 'agam', colors: null },
              home_settings_v2: (data.home_settings_v2 as any) || defaultSettings.home_settings_v2,
              home_settings: (data.home_settings as any), // Keep for backward compatibility
              button_labels: (data.button_labels as any) || {}
            };
            
            console.log('🎨 [AppSettingsProvider] Background color from DB:', settings.theme?.app_background_color);
            console.log('🏠 [AppSettingsProvider] Home settings v2:', settings.home_settings_v2);
            onSettingsChange(settings);
          } else {
            console.log('📝 [AppSettingsProvider] No settings found, using defaults');
            onSettingsChange(defaultSettings);
          }
        } else {
          console.log('🔓 [AppSettingsProvider] Anonymous user - loading public settings');
          // Anonymous user - use public RPC function
          const { data, error } = await supabase.rpc('get_public_app_settings');

          if (error) {
            console.error('❌ [AppSettingsProvider] Error loading public settings:', error);
            console.log('📝 [AppSettingsProvider] Using default settings as fallback');
            onSettingsChange(defaultSettings);
          } else if (data) {
            console.log('✅ [AppSettingsProvider] Public settings loaded successfully:', data);
            // Transform the RPC result into AppSettings format
            const publicData = data as any;
            const settings: AppSettings = {
              id: 1,
              typography: publicData.typography || { source: 'google', googleFont: 'Inter', customFontUrl: null },
              languages: publicData.languages || { default: 'en', enabled: ['en', 'he', 'ar', 'fr', 'es'] },
              theme: publicData.theme || { type: 'preset', preset: 'agam', colors: null },
              home_settings_v2: publicData.home_settings_v2 || defaultSettings.home_settings_v2,
              button_labels: publicData.button_labels || {}
            };
            onSettingsChange(settings);
          } else {
            console.log('📝 [AppSettingsProvider] No public settings found, using defaults');
            onSettingsChange(defaultSettings);
          }
        }
      } catch (error) {
        console.error('💥 [AppSettingsProvider] Exception loading settings:', error);
        onSettingsChange(defaultSettings);
      } finally {
        onLoadingChange(false);
      }
    };

    loadSettings();
  }, []);

  const saveSettings = async (newSettings: Partial<AppSettings>) => {
    try {
      onSavingChange(true);
      console.log('💾 [AppSettingsProvider] Saving settings to app_settings');
      
      // Check authentication status first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.error('❌ [AppSettingsProvider] No authenticated user found');
        toast.error('You must be logged in to save settings');
        return false;
      }

      // Update app_settings table
      const { error } = await supabase
        .from('app_settings')
        .update(newSettings)
        .eq('id', 1);

      if (error) {
        console.error('❌ [AppSettingsProvider] Database error:', error);
        toast.error(`Failed to save settings: ${error.message}`);
        return false;
      }

      // Reload settings to get updated values
      const { data: updatedData, error: fetchError } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (!fetchError && updatedData) {
        // Transform database row to AppSettings format
        const updatedSettings: AppSettings = {
          id: updatedData.id,
          typography: (updatedData.typography as any) || { source: 'google', googleFont: 'Inter', customFontUrl: null },
          languages: (updatedData.languages as any) || { default: 'en', enabled: ['en', 'he', 'ar', 'fr', 'es'] },
          theme: (updatedData.theme as any) || { type: 'preset', preset: 'agam', colors: null },
          home_settings_v2: (updatedData.home_settings_v2 as any) || defaultSettings.home_settings_v2,
          home_settings: (updatedData.home_settings as any), // Keep for backward compatibility
          button_labels: (updatedData.button_labels as any) || {}
        };
        
        console.log('🔄 [AppSettingsProvider] Settings updated, new background color:', updatedSettings.theme?.app_background_color);
        onSettingsChange(updatedSettings);
      }

      console.log('✅ [AppSettingsProvider] Settings saved successfully');
      toast.success('Settings saved successfully');
      return true;
    } catch (error) {
      console.error('💥 [AppSettingsProvider] Exception:', error);
      toast.error('Failed to save settings');
      return false;
    } finally {
      onSavingChange(false);
    }
  };

  const reloadSettings = async () => {
    try {
      console.log('🔄 [AppSettingsProvider] Reloading settings');
      onLoadingChange(true);
      
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) {
        console.error('❌ [AppSettingsProvider] Database error:', error);
        onSettingsChange(defaultSettings);
      } else if (data) {
        console.log('✅ [AppSettingsProvider] Reloaded settings from database');
        
        // Transform database row to AppSettings format
        const settings: AppSettings = {
          id: data.id,
          typography: (data.typography as any) || { source: 'google', googleFont: 'Inter', customFontUrl: null },
          languages: (data.languages as any) || { default: 'en', enabled: ['en', 'he', 'ar', 'fr', 'es'] },
          theme: (data.theme as any) || { type: 'preset', preset: 'agam', colors: null },
          home_settings_v2: (data.home_settings_v2 as any) || defaultSettings.home_settings_v2,
          home_settings: (data.home_settings as any), // Keep for backward compatibility
          button_labels: (data.button_labels as any) || {}
        };
        
        onSettingsChange(settings);
      } else {
        onSettingsChange(defaultSettings);
      }
    } catch (error) {
      console.error('💥 [AppSettingsProvider] Exception reloading settings:', error);
      onSettingsChange(defaultSettings);
    } finally {
      onLoadingChange(false);
    }
  };

  // Expose functions to parent
  useEffect(() => {
    (window as any).__appSettingsProvider = {
      saveSettings,
      reloadSettings
    };
  }, []);

  return <>{children}</>;
}